import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useSocketStore } from '../store/socketStore';
import { useTranslate } from '../i18n';
import { translateApiError } from '../i18n/translations';
import axios from 'axios';
import { API_URL, mediaUrl } from '../config';
import Avatar from '../components/Avatar';
import { setFocusedConversation, consumePendingConversation } from '../notifyFocus';
import {
  FiSend,
  FiPhone,
  FiVideo,
  FiLogOut,
  FiSearch,
  FiX,
  FiLoader,
  FiFilm,
  FiPhoneIncoming,
  FiArrowRight,
  FiPaperclip,
  FiMessageCircle,
  FiUsers
} from 'react-icons/fi';

function idsEqual(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

function otherParticipant(conversation, currentUserId) {
  const nested = conversation?.participant;
  if (nested?.id && !idsEqual(nested.id, currentUserId)) {
    return nested;
  }
  const p1 = conversation?.participant_1;
  const p2 = conversation?.participant_2;
  let otherId = null;
  if (idsEqual(p1, currentUserId)) otherId = p2;
  else if (idsEqual(p2, currentUserId)) otherId = p1;
  else if (nested?.id && !idsEqual(nested.id, currentUserId)) otherId = nested.id;
  else otherId = p2 && !idsEqual(p2, currentUserId) ? p2 : p1;
  if (idsEqual(otherId, currentUserId)) {
    otherId = idsEqual(p1, currentUserId) ? p2 : p1;
  }
  return {
    id: otherId,
    email: nested?.email,
    name: nested?.name
  };
}

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function buildFamilyPeople(contacts, conversations, currentUserId) {
  const convByPeer = new Map();
  for (const conversation of asList(conversations)) {
    const peer = otherParticipant(conversation, currentUserId);
    if (peer?.id && !idsEqual(peer.id, currentUserId)) {
      convByPeer.set(String(peer.id), conversation);
    }
  }

  const contactList = asList(contacts).filter(
    (contact) => contact?.id && !idsEqual(contact.id, currentUserId)
  );
  if (contactList.length) {
    return contactList.map((contact) => {
      const conversation =
        convByPeer.get(String(contact.id)) ||
        (contact.conversationId
          ? {
              id: contact.conversationId,
              last_message: contact.lastMessage,
              participant: contact,
              participant_1: currentUserId,
              participant_2: contact.id
            }
          : null);
      return {
        conversation,
        peer: {
          id: contact.id,
          email: contact.email,
          name: contact.name,
          avatar_url: contact.avatar_url
        }
      };
    });
  }

  const rows = new Map();
  for (const conversation of asList(conversations)) {
    const peer = otherParticipant(conversation, currentUserId);
    const peerIsSelf = idsEqual(peer?.id, currentUserId);
    const key =
      peer?.id && !peerIsSelf ? String(peer.id) : conversation?.id ? `conv:${conversation.id}` : null;
    if (!key || rows.has(key)) continue;
    rows.set(key, { conversation, peer });
  }
  return [...rows.values()];
}

function displayName(person) {
  if (!person) return '';
  if (typeof person === 'string') return person.split('@')[0];
  return person.name || person.email?.split('@')[0] || '';
}

function timeGreeting(t) {
  const hour = new Date().getHours();
  if (hour < 5) return t('goodNight');
  if (hour < 12) return t('goodMorning');
  if (hour < 17) return t('goodAfternoon');
  if (hour < 21) return t('goodEvening');
  return t('goodNight');
}

function lastMessagePreview(raw, t) {
  if (!raw) return t('noMessages');
  const value = String(raw);
  if (/\.(mp4|webm|mov|quicktime)(\?|$)/i.test(value)) return t('videoMessage');
  if (/\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(value) || /\/uploads\//.test(value)) {
    return t('imageMessage');
  }
  return value;
}

export default function ChatPage() {
  const t = useTranslate();

  const [conversations, setConversations] = useState([]);
  const [familyContacts, setFamilyContacts] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typingTimer, setTypingTimer] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [listTab, setListTab] = useState('chats');

  const { token, logout, user } = useAuthStore();
  const { socket, connected, onlineUsers, typingUsers } = useSocketStore();
  const { initSocket, disconnectSocket } = useSocketStore();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const selectedIdRef = useRef(null);
  const pendingOpenRef = useRef(consumePendingConversation());

  useEffect(() => {
    if (token && !socket?.connected) {
      initSocket(token);
    }
  }, [token, socket?.connected, initSocket]);

  useEffect(() => {
    selectedIdRef.current = selectedConversation?.id || null;
    setFocusedConversation(selectedConversation?.id || null);
    return () => setFocusedConversation(null);
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (!socket) return;

    const onMessage = (message) => {
      const conversationId = message.conversationId;
      if (selectedIdRef.current === conversationId) {
        setConversationMessages((prev) => {
          if (message.id && prev.some((item) => item.id === message.id)) return prev;
          return [...prev, message];
        });
      }
      loadFamily();
    };

    const onIncoming = (data) => {
      if (data.callerId === user?.id) return;
      setIncomingCall(data);
    };

    socket.on('new-message', onMessage);
    socket.on('incoming-call', onIncoming);
    socket.on('call-status', (data) => {
      if (data.status === 'rejected' || data.status === 'ended') {
        setIncomingCall(null);
      }
    });

    return () => {
      socket.off('new-message', onMessage);
      socket.off('incoming-call', onIncoming);
      socket.off('call-status');
    };
  }, [socket, user?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages]);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadFamily = async () => {
    const authToken = useAuthStore.getState().token;
    if (!authToken) return;
    const headers = { Authorization: `Bearer ${authToken}` };
    try {
      const [listRes, contactsRes] = await Promise.all([
        axios.get(`${API_URL}/conversations/list`, { headers }).catch((err) => {
          if (err.response?.status === 401 || err.response?.status === 403) throw err;
          return { data: [] };
        }),
        axios.get(`${API_URL}/users/contacts`, { headers }).catch((err) => {
          if (err.response?.status === 401 || err.response?.status === 403) throw err;
          return { data: [] };
        })
      ]);
      const list = asList(listRes.data);
      const contacts = asList(contactsRes.data);
      setConversations(list);
      setFamilyContacts(contacts);
      const pending = pendingOpenRef.current;
      if (pending) {
        const found =
          list.find((item) => item.id === pending) ||
          contacts
            .filter((item) => item.conversationId === pending)
            .map((item) => ({
              id: item.conversationId,
              last_message: item.lastMessage,
              participant: item
            }))[0];
        if (found) {
          pendingOpenRef.current = null;
          setSelectedConversation(found);
          setMobileShowChat(true);
          loadMessages(found.id);
        }
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
        navigate('/login');
        return;
      }
      console.error('Failed to load conversations:', err);
    }
  };

  useEffect(() => {
    if (token) {
      loadFamily();
      const interval = setInterval(loadFamily, 8000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const loadMessages = async (conversationId) => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/messages/${conversationId}?limit=100`, {
        headers: authHeaders
      });
      setConversationMessages(response.data || []);
      socket?.emit('join-conversation', conversationId);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMobileShowChat(true);
    loadMessages(conversation.id);
  };

  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setUsers([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/users/search?q=${encodeURIComponent(query)}`, {
        headers: authHeaders
      });
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to search users:', err);
    }
  };

  const handleStartConversation = async (recipientId) => {
    try {
      const response = await axios.post(
        `${API_URL}/conversations/direct`,
        { recipientId },
        { headers: authHeaders }
      );
      await loadFamily();
      const list = await axios.get(`${API_URL}/conversations/list`, { headers: authHeaders });
      const created = (list.data || []).find((item) => item.id === response.data.id);
      handleSelectConversation(
        created || {
          id: response.data.id,
          type: 'direct',
          participant: { id: recipientId }
        }
      );
      setSearchQuery('');
      setUsers([]);
      setListTab('chats');
    } catch (err) {
      console.error('Failed to start conversation:', err);
      setError(t('error'));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation) return;
    const content = messageText;
    setMessageText('');
    try {
      const response = await axios.post(
        `${API_URL}/messages`,
        {
          conversationId: selectedConversation.id,
          content,
          type: 'text'
        },
        { headers: authHeaders }
      );
      setConversationMessages((prev) => {
        if (response.data.id && prev.some((item) => item.id === response.data.id)) return prev;
        return [...prev, { ...response.data, sender_id: user?.id }];
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessageText(content);
      setError(t('error'));
    }
  };

  const handleUpload = async (file) => {
    if (!file || !selectedConversation) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('conversationId', selectedConversation.id);
      const response = await axios.post(`${API_URL}/messages/upload`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      setConversationMessages((prev) => {
        if (response.data.id && prev.some((item) => item.id === response.data.id)) return prev;
        return [...prev, { ...response.data, sender_id: user?.id }];
      });
    } catch (err) {
      console.error('Failed to upload file:', err);
      setError(translateApiError(err.response?.data?.error) || t('error'));
    } finally {
      setUploading(false);
    }
  };

  const handleTyping = () => {
    if (!selectedConversation) return;
    socket?.emit('typing', {
      conversationId: selectedConversation.id,
      userId: user?.id,
      userName: user?.email
    });
    if (typingTimer) clearTimeout(typingTimer);
    setTypingTimer(
      setTimeout(() => {
        socket?.emit('stop-typing', { conversationId: selectedConversation.id });
      }, 2000)
    );
  };

  const handleStartCall = async (type = 'audio') => {
    if (!selectedConversation) return;
    const peer = otherParticipant(selectedConversation, user?.id);
    if (!peer?.id) {
      setError(t('error'));
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/calls`,
        { recipientId: peer.id, callType: type },
        { headers: authHeaders }
      );
      navigate(`/call/${response.data.id}?peer=${peer.id}&role=initiator&type=${type}`);
    } catch (err) {
      console.error('Failed to start call:', err);
      setError(t('error'));
    }
  };

  const acceptIncomingCall = async () => {
    if (!incomingCall) return;
    try {
      await axios.put(
        `${API_URL}/calls/${incomingCall.id}`,
        { status: 'accepted' },
        { headers: authHeaders }
      );
      socket?.emit('call-accept', {
        callId: incomingCall.id,
        callerId: incomingCall.callerId,
        recipientId: user?.id
      });
      const callType = incomingCall.type || incomingCall.callType || 'audio';
      const callId = incomingCall.id;
      const callerId = incomingCall.callerId;
      setIncomingCall(null);
      navigate(`/call/${callId}?peer=${callerId}&role=callee&type=${callType}`);
    } catch (err) {
      console.error('Failed to accept call:', err);
      setError(t('error'));
    }
  };

  const rejectIncomingCall = async () => {
    if (!incomingCall) return;
    try {
      await axios.put(
        `${API_URL}/calls/${incomingCall.id}`,
        { status: 'rejected', endedAt: new Date().toISOString() },
        { headers: authHeaders }
      );
      socket?.emit('call-reject', {
        callId: incomingCall.id,
        callerId: incomingCall.callerId,
        recipientId: user?.id
      });
    } catch (err) {
      console.error('Failed to reject call:', err);
    } finally {
      setIncomingCall(null);
    }
  };

  const handleLogout = () => {
    disconnectSocket();
    logout();
    navigate('/login');
  };

  const renderMessageBody = (msg) => {
    if (msg.type === 'image') {
      return (
        <a href={mediaUrl(msg.content)} target="_blank" rel="noreferrer">
          <img
            src={mediaUrl(msg.content)}
            alt=""
            width={240}
            height={160}
            className="max-w-full rounded-2xl max-h-64 object-cover"
          />
        </a>
      );
    }
    if (msg.type === 'video') {
      return (
        <video src={mediaUrl(msg.content)} controls className="max-w-full rounded-2xl max-h-64" />
      );
    }
    return <p className="break-words leading-relaxed">{msg.content}</p>;
  };

  const selectedPeer = selectedConversation
    ? otherParticipant(selectedConversation, user?.id)
    : null;

  const incomingPeer = incomingCall
    ? conversations
        .map((conversation) => otherParticipant(conversation, user?.id))
        .find((peer) => peer?.id === incomingCall.callerId) || {
        id: incomingCall.callerId,
        name: incomingCall.callerName
      }
    : null;

  const familyPeople = buildFamilyPeople(familyContacts, conversations, user?.id);
  const contactPeople = familyPeople;

  const handleOpenPerson = (conversation, peer) => {
    if (conversation?.id) {
      handleSelectConversation(conversation);
      return;
    }
    if (peer?.id) {
      handleStartConversation(peer.id);
    }
  };

  return (
    <div className="flex min-h-dvh bg-canvas overflow-hidden">
      {incomingCall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40">
          <div
            role="dialog"
            aria-labelledby="incoming-call-title"
            className="nest-card p-6 max-w-sm w-full text-center"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-nest text-ink">
              <FiPhoneIncoming size={28} aria-hidden="true" />
            </div>
            {incomingPeer && (
              <div className="mb-3 flex justify-center">
                <Avatar email={incomingPeer.name || incomingPeer.email} size="lg" />
              </div>
            )}
            <h3 id="incoming-call-title" className="text-lg font-bold text-ink mb-1">
              {t('incomingCall')}
            </h3>
            <p className="text-ink-muted mb-6">
              {displayName(incomingPeer) ? `${displayName(incomingPeer)} · ` : ''}
              {incomingCall.type === 'video' || incomingCall.callType === 'video'
                ? t('incomingVideo')
                : t('incomingAudio')}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={acceptIncomingCall}
                className="min-h-[48px] flex-1 rounded-2xl bg-accent text-white font-semibold cursor-pointer hover:opacity-90 transition"
              >
                {t('acceptCall')}
              </button>
              <button
                onClick={rejectIncomingCall}
                className="min-h-[48px] flex-1 rounded-2xl bg-red-500 text-white font-semibold cursor-pointer hover:opacity-90 transition"
              >
                {t('rejectCall')}
              </button>
            </div>
          </div>
        </div>
      )}

      <aside
        className={`${
          mobileShowChat ? 'hidden' : 'flex'
        } md:flex w-full md:w-80 lg:w-[26rem] flex-col bg-canvas relative`}
      >
        <div className="px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-3">
          <div className="flex items-center gap-3 mb-4">
            <span className="relative inline-flex shrink-0">
              <img
                src="/apple-touch-icon.png"
                alt=""
                width={56}
                height={56}
                className="h-14 w-14 rounded-[1.25rem] shadow-float bg-white object-cover"
              />
              {connected ? (
                <span className="absolute bottom-0 end-0 h-3 w-3 rounded-full bg-accent ring-2 ring-white" />
              ) : null}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink-muted">
                {listTab === 'contacts' ? t('contacts') : timeGreeting(t)}
              </p>
              <h1 className="text-xl font-bold text-ink truncate">
                {displayName(user) || t('appName')}
              </h1>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="icon-btn bg-white shadow-float"
              aria-label={t('logout')}
            >
              <FiLogOut size={18} />
            </button>
          </div>

          <div className="relative mb-3">
            <FiSearch className="absolute start-3 top-1/2 -translate-y-1/2 text-ink-faint" size={18} />
            <input
              type="search"
              placeholder={t('searchContacts')}
              value={searchQuery}
              onChange={(e) => handleSearchUsers(e.target.value)}
              className="w-full min-h-[48px] ps-10 pe-10 rounded-2xl bg-white shadow-float border border-transparent focus:border-line text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setUsers([]);
                }}
                className="absolute end-1 top-1/2 -translate-y-1/2 icon-btn h-9 w-9"
                aria-label={t('close')}
              >
                <FiX size={16} />
              </button>
            )}
          </div>
          {users.length > 0 && listTab === 'chats' && (
            <div className="mb-3 nest-card overflow-hidden max-h-48 overflow-y-auto">
              {users.map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleStartConversation(u.id)}
                  className="w-full flex items-center gap-3 px-3 py-3 hover:bg-canvas text-start cursor-pointer"
                >
                  <Avatar email={u.name || u.email} size="sm" />
                  <span className="text-sm font-medium text-ink truncate">{displayName(u)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-28">
          {listTab === 'chats' ? (
            <>
              <div className="bg-nest rounded-[1.75rem] px-5 py-4 mb-4 shadow-card">
                <p className="text-sm font-semibold text-ink">{t('familyHero')}</p>
              </div>

              {familyPeople.length > 0 ? (
                <div className="space-y-3">
                  {familyPeople.map(({ conversation, peer }) => {
                    const active = selectedConversation?.id && conversation?.id === selectedConversation.id;
                    return (
                      <button
                        key={peer?.id || conversation?.id}
                        onClick={() => handleOpenPerson(conversation, peer)}
                        className={`w-full flex items-center gap-3 rounded-[1.5rem] px-3 py-3 text-start cursor-pointer transition duration-200 active:scale-[0.99] ${
                          active ? 'bg-nest shadow-float' : 'bg-white shadow-card hover:shadow-float'
                        }`}
                      >
                        <Avatar
                          email={peer?.name || peer?.email}
                          online={onlineUsers.has(peer?.id)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex justify-between gap-2 items-center">
                            <span className="font-bold text-ink text-sm truncate">
                              {displayName(peer) || t('chat')}
                            </span>
                          </span>
                          <span className="block text-xs text-ink-muted truncate mt-0.5">
                            {lastMessagePreview(conversation?.last_message, t)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="px-2 py-12 text-center nest-card p-6">
                  <p className="text-ink-muted text-sm leading-relaxed">{t('emptyListHint')}</p>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              {users.length > 0
                ? users.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => handleStartConversation(u.id)}
                      className="w-full flex items-center gap-3 rounded-[1.5rem] px-3 py-3 bg-white shadow-card text-start cursor-pointer hover:shadow-float transition"
                    >
                      <Avatar email={u.name || u.email} size="md" />
                      <span className="text-sm font-bold text-ink truncate">{displayName(u)}</span>
                    </button>
                  ))
                : contactPeople.length > 0
                  ? contactPeople.map(({ conversation, peer }) => (
                      <button
                        key={peer?.id || conversation?.id}
                        onClick={() => handleOpenPerson(conversation, peer)}
                        className="w-full flex items-center gap-3 rounded-[1.5rem] px-3 py-3 bg-white shadow-card text-start cursor-pointer hover:shadow-float transition"
                      >
                        <Avatar
                          email={peer?.name || peer?.email}
                          online={onlineUsers.has(peer?.id)}
                        />
                        <span className="min-w-0 flex-1 text-start">
                          <span className="block font-bold text-ink text-sm truncate">
                            {displayName(peer)}
                          </span>
                          <span className="block text-xs text-ink-muted">
                            {onlineUsers.has(peer?.id) ? t('online') : t('offline')}
                          </span>
                        </span>
                      </button>
                    ))
                  : (
                    <div className="nest-card p-6 text-center">
                      <p className="text-ink-muted text-sm leading-relaxed">{t('emptyListHint')}</p>
                    </div>
                    )}
            </div>
          )}
        </div>

        <nav
          className="absolute bottom-0 inset-x-0 z-20 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-4"
          aria-label={t('appName')}
        >
          <div className="flex items-center justify-around bg-white rounded-full shadow-nav px-2 py-2">
            <button
              type="button"
              onClick={() => setListTab('chats')}
              className={`flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] px-5 rounded-full text-sm font-semibold cursor-pointer transition duration-200 ${
                listTab === 'chats' ? 'bg-primary text-white' : 'text-ink-muted'
              }`}
              aria-current={listTab === 'chats' ? 'page' : undefined}
            >
              <FiMessageCircle size={18} aria-hidden="true" />
              {t('chat')}
            </button>
            <button
              type="button"
              onClick={() => setListTab('contacts')}
              className={`flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] px-5 rounded-full text-sm font-semibold cursor-pointer transition duration-200 ${
                listTab === 'contacts' ? 'bg-primary text-white' : 'text-ink-muted'
              }`}
              aria-current={listTab === 'contacts' ? 'page' : undefined}
            >
              <FiUsers size={18} aria-hidden="true" />
              {t('contacts')}
            </button>
          </div>
        </nav>
      </aside>

      <section
        className={`${
          mobileShowChat ? 'flex' : 'hidden'
        } md:flex flex-1 flex-col min-w-0 bg-canvas`}
      >
        {selectedConversation ? (
          <>
            <header className="flex items-center gap-2 px-3 sm:px-5 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
              <button
                type="button"
                className="md:hidden icon-btn bg-white shadow-float"
                onClick={() => setMobileShowChat(false)}
                aria-label={t('back')}
              >
                <FiArrowRight size={20} />
              </button>
              <div className="min-w-0 flex-1 text-center md:text-start">
                <h2 className="font-bold text-ink truncate">
                  {displayName(selectedPeer)}
                </h2>
                <p className="text-xs text-ink-muted">
                  {onlineUsers.has(selectedPeer?.id) ? t('online') : t('offline')}
                </p>
              </div>
              <Avatar
                email={selectedPeer?.name || selectedPeer?.email}
                online={onlineUsers.has(selectedPeer?.id)}
                size="sm"
              />
              <button
                onClick={() => handleStartCall('audio')}
                className="icon-btn bg-white shadow-float"
                aria-label={t('startCall')}
              >
                <FiPhone size={18} />
              </button>
              <button
                onClick={() => handleStartCall('video')}
                className="icon-btn bg-white shadow-float"
                aria-label={t('startVideo')}
              >
                <FiVideo size={18} />
              </button>
            </header>

            {error && (
              <div
                role="alert"
                className="mx-4 mb-2 rounded-2xl bg-red-50 px-4 py-2 text-red-700 text-sm flex justify-between items-center"
              >
                <span>{error}</span>
                <button onClick={() => setError('')} className="icon-btn h-9 w-9" aria-label={t('close')}>
                  <FiX size={16} />
                </button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-6 py-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center h-full text-ink-faint">
                  <FiLoader className="animate-spin" size={28} />
                </div>
              ) : conversationMessages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-ink-muted text-sm">{t('noMessages')}</p>
                </div>
              ) : (
                conversationMessages.map((msg, idx) => {
                  const isOwn = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id || idx}
                      className={`flex items-end gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      {!isOwn && (
                        <Avatar
                          email={selectedPeer?.name || selectedPeer?.email}
                          size="sm"
                        />
                      )}
                      <div className={`max-w-[80%] sm:max-w-sm ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                        <div
                          className={`px-4 py-3 shadow-bubble ${
                            isOwn
                              ? 'bg-primary text-white rounded-[1.5rem] rounded-ee-md'
                              : 'bg-white text-ink rounded-[1.5rem] rounded-es-md'
                          }`}
                        >
                          {renderMessageBody(msg)}
                        </div>
                        <span className="text-[11px] text-ink-faint mt-1 px-1">
                          {msg.created_at
                            ? new Date(msg.created_at).toLocaleTimeString('fa-IR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : ''}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
              {typingUsers[selectedConversation.id] &&
                typingUsers[selectedConversation.id].userId !== user?.id && (
                  <div className="flex justify-start items-end gap-2">
                    <Avatar email={selectedPeer?.name || selectedPeer?.email} size="sm" />
                    <div className="bg-white px-4 py-3 rounded-[1.5rem] shadow-bubble">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-ink-faint rounded-full animate-bounce"></span>
                        <span
                          className="w-2 h-2 bg-ink-faint rounded-full animate-bounce"
                          style={{ animationDelay: '0.1s' }}
                        ></span>
                        <span
                          className="w-2 h-2 bg-ink-faint rounded-full animate-bounce"
                          style={{ animationDelay: '0.2s' }}
                        ></span>
                      </div>
                    </div>
                  </div>
                )}
              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={handleSendMessage}
              className="px-3 sm:px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            >
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  handleUpload(file);
                }}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  handleUpload(file);
                }}
              />
              <div className="flex gap-2 items-center">
                <div className="flex-1 flex items-center gap-1 min-h-[52px] ps-2 pe-3 rounded-full bg-white shadow-nav">
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="icon-btn"
                    aria-label={t('sendImage')}
                    disabled={uploading}
                  >
                    <FiPaperclip size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    className="icon-btn"
                    aria-label={t('sendVideo')}
                    disabled={uploading}
                  >
                    <FiFilm size={20} />
                  </button>
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => {
                      setMessageText(e.target.value);
                      handleTyping();
                    }}
                    placeholder={uploading ? t('uploading') : t('typeMessage')}
                    className="flex-1 min-h-[44px] bg-transparent border-0 focus:ring-0 text-ink placeholder:text-ink-faint"
                  />
                </div>
                <button
                  type="submit"
                  disabled={!messageText.trim() || uploading}
                  className="h-12 w-12 rounded-full bg-primary text-white inline-flex items-center justify-center shadow-nav cursor-pointer hover:opacity-90 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('send')}
                >
                  {uploading ? (
                    <FiLoader className="animate-spin" size={18} />
                  ) : (
                    <FiSend size={18} className="-scale-x-100" />
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <img
                src="/apple-touch-icon.png"
                alt=""
                width={64}
                height={64}
                className="mx-auto mb-4 h-16 w-16 rounded-3xl shadow-float bg-white object-cover"
              />
              <h2 className="text-xl font-bold text-ink mb-2">{t('appName')}</h2>
              <p className="text-ink-muted leading-relaxed">{t('emptyListHint')}</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
